import { useState, useEffect } from 'react';
import { Database, Search, ChevronLeft, ChevronRight, Loader2, Table2, FileJson } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { adminToolsService } from '../../services';
import { toast } from 'sonner';

export const DbLookupPanel = () => {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queryLoading, setQueryLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ skip: 0, limit: 50, total: 0 });

  useEffect(() => { loadCollections(); }, []);

  const loadCollections = async () => {
    setLoading(true);
    try {
      const data = await adminToolsService.getCollections();
      setCollections(data.collections || []);
    } catch (e) {
      toast.error('Fehler beim Laden der Collections');
    } finally {
      setLoading(false);
    }
  };

  const queryCollection = async (name, skip = 0, searchTerm = '') => {
    setQueryLoading(true);
    setSelectedCollection(name);
    try {
      const params = { skip, limit: pagination.limit };
      if (searchTerm) params.search = searchTerm;
      const data = await adminToolsService.queryCollection(name, params);
      setDocuments(data.documents || []);
      setPagination(prev => ({ ...prev, skip, total: data.total }));
    } catch (e) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setQueryLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (selectedCollection) queryCollection(selectedCollection, 0, search);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-primary)' }} /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Database className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
        <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Datenbank Explorer</h3>
        <Badge variant="outline">Nur Lesen</Badge>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {/* Collection List */}
        <div className="col-span-1 space-y-1" data-testid="db-collection-list">
          {collections.map(c => (
            <button
              key={c.name}
              onClick={() => { setSearch(''); queryCollection(c.name); }}
              data-testid={`db-collection-${c.name}`}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex justify-between items-center ${
                selectedCollection === c.name ? 'font-medium' : ''
              }`}
              style={{
                backgroundColor: selectedCollection === c.name ? 'var(--color-primary)' : 'transparent',
                color: selectedCollection === c.name ? 'var(--color-text-on-primary)' : 'var(--color-text-primary)'
              }}
            >
              <span className="flex items-center gap-2">
                <Table2 className="w-3 h-3" />
                {c.name}
              </span>
              <Badge variant={selectedCollection === c.name ? 'secondary' : 'outline'} className="text-xs">
                {c.document_count}
              </Badge>
            </button>
          ))}
        </div>

        {/* Document View */}
        <div className="col-span-3">
          {selectedCollection ? (
            <Card style={{ borderColor: 'var(--color-border)' }}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base" style={{ color: 'var(--color-text-primary)' }}>
                    {selectedCollection} <span className="font-normal text-sm" style={{ color: 'var(--color-text-secondary)' }}>({pagination.total} Dokumente)</span>
                  </CardTitle>
                  <form onSubmit={handleSearch} className="flex gap-2">
                    <Input
                      placeholder="Suchen..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      className="w-48 h-8 text-sm"
                      data-testid="db-search-input"
                    />
                    <Button type="submit" size="sm" variant="outline"><Search className="w-3 h-3" /></Button>
                  </form>
                </div>
              </CardHeader>
              <CardContent>
                {queryLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-primary)' }} /></div>
                ) : documents.length === 0 ? (
                  <p className="text-center py-8" style={{ color: 'var(--color-text-secondary)' }}>Keine Dokumente gefunden</p>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-auto">
                    {documents.map((doc, i) => (
                      <pre
                        key={i}
                        className="text-xs p-3 rounded-lg overflow-x-auto border"
                        style={{ backgroundColor: 'var(--color-background)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
                        data-testid={`db-document-${i}`}
                      >
                        {JSON.stringify(doc, null, 2)}
                      </pre>
                    ))}
                  </div>
                )}
                {/* Pagination */}
                {pagination.total > pagination.limit && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
                    <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {pagination.skip + 1}–{Math.min(pagination.skip + pagination.limit, pagination.total)} von {pagination.total}
                    </span>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" disabled={pagination.skip === 0}
                        onClick={() => queryCollection(selectedCollection, Math.max(0, pagination.skip - pagination.limit), search)}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" disabled={pagination.skip + pagination.limit >= pagination.total}
                        onClick={() => queryCollection(selectedCollection, pagination.skip + pagination.limit, search)}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col items-center justify-center py-16" style={{ color: 'var(--color-text-secondary)' }}>
              <FileJson className="w-12 h-12 mb-3 opacity-40" />
              <p>Collection auswählen um Daten anzuzeigen</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
